/**
 * Section + SectionHead: contenedor de sección alineado al handoff oficial.
 *
 * Sprint 14 S14-06a (revisión 2). Replica `.cb-section` +
 * `.cb-section__header` + `.cb-section__title` + `.cb-section__link` del
 * `calbucura-design-handoff/design-system/components.css`.
 *
 * Decisión visual confirmada por review en /dev/showcase (2026-05-15):
 * variant card con padding interno + fondo de card. Title 13px medium.
 * Look sobrio y profesional — alineado al Dashboard de Inicio aprobado
 * en Sprint 11 v2.
 *
 * Uso:
 *   <Section>
 *     <SectionHead title="Datos personales" link="Editar" onLinkClick={...} />
 *     <div>{contenido}</div>
 *   </Section>
 *
 * Sections sucesivas separan con margin-top: 10px (var(--cb-space-3)).
 * Modificadores: noTop (sin padding superior), tight (padding ajustado).
 */
import type { ReactNode, MouseEventHandler } from 'react';

interface SectionProps {
  children: ReactNode;
  /** Sin padding superior (para encadenar con sección previa). */
  noTop?: boolean;
  /** Padding ajustado (top compacto, bottom estándar). */
  tight?: boolean;
  className?: string;
}

export function Section({
  children,
  noTop = false,
  tight = false,
  className = '',
}: SectionProps) {
  // px-5 py-4 = padding 16px 20px (var(--cb-space-6) var(--cb-space-8))
  // bg-bg-card + [&+&]:mt-3 alineado al .cb-section del handoff.
  const padding = noTop
    ? 'pt-0 pb-4 px-5'
    : tight
      ? 'pt-1 pb-5 px-5'
      : 'py-4 px-5';

  return (
    <section
      className={`${padding} bg-bg-card [&+&]:mt-3 ${className}`.trim()}
    >
      {children}
    </section>
  );
}

interface SectionHeadProps {
  title?: ReactNode;
  /** Texto del enlace a la derecha (ej. "Ver todos", "Editar"). */
  link?: ReactNode;
  /** Handler del enlace. Si está presente, link se renderiza como <button>. */
  onLinkClick?: MouseEventHandler<HTMLButtonElement>;
  /** Acciones custom a la derecha (cuando link/onLinkClick no aplica). */
  actions?: ReactNode;
  className?: string;
}

export function SectionHead({
  title,
  link,
  onLinkClick,
  actions,
  className = '',
}: SectionHeadProps) {
  return (
    <div
      className={`flex justify-between items-baseline mb-3 ${className}`.trim()}
    >
      {title && (
        <div className="text-md font-medium text-text-primary">{title}</div>
      )}
      {link && (
        <button
          type="button"
          onClick={onLinkClick}
          className="text-sm text-text-secondary hover:text-text-primary transition-colors duration-base bg-transparent border-0 cursor-pointer p-0"
        >
          {link}
        </button>
      )}
      {actions && !link && <div>{actions}</div>}
    </div>
  );
}
